import { useDebounce } from '@uidotdev/usehooks';
import { X } from 'lucide-react';
import * as React from 'react';

import {
  Combobox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/ui';
import { DateRangePicker } from '@/design-system/components/ui/date-range-picker';
import { cn } from '@/design-system/lib/utils';

import { type FilterChipDef, UNSET } from './types';

export interface FilterChipProps {
  def: FilterChipDef;
  /** Current value: string (single chips) or string[] (multi combobox). */
  value: string | string[];
  /** Persisted value of the chip when active (e.g. for date-range, ISO strings). */
  rangeValue?: { from: Date | null; to: Date | null };
  /** Default value used to determine "active" state and to write back on clear. */
  defaultValue: string | string[];
  isActive: boolean;
  onChange: (next: string | string[] | null) => void;
  /** For date-range chips: separate writers for the satellite from/to keys. */
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  /**
   * Visual layout. `chip` (default) uses the small inline trigger with a
   * 240px max-width — what the desktop toolbar renders. `field` switches to
   * the full-width default-size trigger, suitable for stacking inside a
   * mobile Sheet.
   */
  layout?: 'chip' | 'field';
}

/**
 * Chip-style sizing applied to design-system trigger primitives so they sit
 * inline in the toolbar instead of stretching to the full row width. The
 * primitives keep their default 58px height — the label-above-value pattern
 * needs the room — and own the icon + active state.
 *
 * `max-w-[240px]` caps how wide a single chip can grow when its value is long
 * (e.g. a verbose conversion name) so it can't push neighbouring chips off the
 * row. The primitives' inner value span already has `min-w-0 truncate`, so
 * overflow gets a clean ellipsis.
 */
const TRIGGER_CHIP_CLASSES = 'tw:w-auto tw:max-w-[240px]';
const TRIGGER_FIELD_CLASSES = 'tw:w-full';

export const FilterChip = ({
  def,
  value,
  rangeValue,
  isActive,
  onChange,
  onDateRangeChange,
  layout = 'chip',
}: FilterChipProps) => {
  const isField = layout === 'field';
  const triggerSize = isField ? 'default' : 'small';
  const triggerWidthClasses = isField ? TRIGGER_FIELD_CLASSES : TRIGGER_CHIP_CLASSES;

  // ── Select chip: render the Select primitive directly ──────────────────
  if (def.type === 'select') {
    const stringValue = typeof value === 'string' ? value : UNSET;
    const visibleOptions = def.options.filter((o) => o.value !== UNSET);
    return (
      <Select value={stringValue || UNSET} onValueChange={(next) => onChange(next === UNSET ? null : next)}>
        <SelectTrigger
          icon={def.icon}
          active={isActive}
          label={def.label}
          size={triggerSize}
          disabled={def.disabled}
          className={cn('tw:bg-card', triggerWidthClasses)}
          onClear={isActive ? () => onChange(null) : undefined}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {visibleOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // ── Combobox chip: render the Combobox primitive directly ──────────────
  if (def.type === 'combobox') {
    if (def.multi) {
      const arrayValue = Array.isArray(value) ? value : [];
      return (
        <Combobox
          multiple
          clearable
          options={def.options.filter((o) => o.value !== UNSET)}
          value={arrayValue}
          onValueChange={(next) => onChange(next.length === 0 ? null : next)}
          placeholder=""
          icon={def.icon}
          active={isActive}
          label={def.label}
          size={triggerSize}
          disabled={def.disabled}
          selectAllLabel={def.selectAllLabel}
          className={cn(triggerWidthClasses)}
        />
      );
    }

    // Single combobox.
    const rawValue = typeof value === 'string' ? value : UNSET;
    const stringValue = rawValue === UNSET ? '' : rawValue;
    return (
      <Combobox
        clearable
        freeSolo={def.freeSolo}
        groupLabel={def.groupLabel}
        options={def.options.filter((o) => o.value !== UNSET)}
        value={stringValue}
        onValueChange={(next) => onChange(!next || next === UNSET ? null : next)}
        placeholder=""
        icon={def.icon}
        active={isActive}
        label={def.label}
        size={triggerSize}
        disabled={def.disabled}
        className={cn(triggerWidthClasses)}
      />
    );
  }

  // ── Date-range chip: existing primitive, sized inline ──────────────────
  if (def.type === 'date-range') {
    const range = rangeValue ?? { from: null, to: null };
    return (
      <DateRangePicker
        value={range}
        onChange={(next) => onDateRangeChange?.(next)}
        enableTime={def.enableTime}
        placeholder="Select date range"
        label={def.label}
        active={isActive}
        size={triggerSize}
        className={cn(triggerWidthClasses)}
        onClear={isActive ? () => onDateRangeChange?.({ from: null, to: null }) : undefined}
      />
    );
  }

  // ── Address chip: chip trigger + Input inside a Popover ────────────────
  if (def.type === 'address') {
    return <AddressChip def={def} value={value} isActive={isActive} onChange={onChange} layout={layout} />;
  }

  // ── Number-range: not implemented for v1 (no consumer needs it yet) ────
  return <span className="tw:text-xs tw:text-muted-foreground tw:italic">number-range chip not yet implemented</span>;
};

// ── Address chip helper ────────────────────────────────────────────────────
// Keeps the popover + Input UX for free-text addresses since there is no
// design-system "address" primitive to reuse directly.

interface AddressChipProps {
  def: Extract<FilterChipDef, { type: 'address' }>;
  value: string | string[];
  isActive: boolean;
  onChange: (next: string | string[] | null) => void;
  layout?: 'chip' | 'field';
}

const AddressChip = ({ def, value, isActive, onChange, layout = 'chip' }: AddressChipProps) => {
  const [open, setOpen] = React.useState(false);
  const rawValue = typeof value === 'string' ? value : '';
  // Treat the UNSET sentinel as "no value" so it never leaks into the input.
  const stringValue = rawValue === UNSET ? '' : rawValue;
  const Icon = def.icon;
  const isField = layout === 'field';

  // Local input state — lets the user type freely even when their current
  // input fails `validate` (we only gate the URL push, not the typing).
  const [local, setLocal] = React.useState(stringValue);
  const debounceMs = def.debounceMs ?? 600;
  const debounced = useDebounce(local, debounceMs);

  // Push debounced value to URL, gating non-empty values by `validate`.
  React.useEffect(() => {
    if (!debounced) {
      onChange(null);
      return;
    }
    if (def.validate && !def.validate(debounced)) {
      return;
    }
    onChange(debounced);
  }, [debounced]);

  // Sync URL → local (e.g. clearAll, hydration, external resets).
  React.useEffect(() => {
    setLocal(stringValue);
  }, [stringValue]);

  const showClear = isActive && !def.disabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('tw:relative', isField ? 'tw:w-full' : 'tw:w-auto')}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={def.disabled}
            className={cn(
              'tw:group tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:rounded-[10px] tw:border tw:bg-card tw:px-3 tw:py-2 tw:text-sm tw:transition-[colors,filter] tw:duration-200 tw:ease-in-out',
              'tw:hover:not-focus:brightness-95 tw:focus:outline-none',
              'tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
              isField ? 'tw:min-h-[58px]' : 'tw:h-[38px] tw:max-w-[240px]',
              isActive
                ? 'tw:border-fuul-blue tw:text-fuul-blue'
                : 'tw:border-muted tw:focus:border-fuul-blue tw:focus-visible:border-fuul-blue tw:data-[state=open]:border-fuul-blue',
              showClear && 'tw:pr-9',
            )}
          >
            {Icon && (
              <Icon
                aria-hidden
                className={cn(
                  'tw:mr-2 tw:h-4 tw:w-4 tw:shrink-0 tw:transition-colors tw:duration-200 tw:ease-in-out',
                  isActive ? 'tw:text-fuul-blue' : 'tw:text-muted-foreground tw:group-data-[state=open]:text-fuul-blue',
                )}
              />
            )}
            <div
              className={cn(
                'tw:flex tw:flex-1 tw:text-left tw:min-w-0',
                isField ? 'tw:flex-col tw:items-start' : 'tw:flex-row tw:items-center tw:gap-1.5',
              )}
            >
              {def.label && (
                <span
                  className={cn(
                    'tw:font-inter tw:font-normal tw:tracking-[-0.01em] tw:transition-colors tw:duration-200 tw:ease-in-out tw:shrink-0',
                    isField ? 'tw:text-xs tw:leading-5' : 'tw:text-sm',
                    isActive
                      ? 'tw:text-fuul-blue'
                      : 'tw:text-label tw:group-focus:text-fuul-blue tw:group-data-[state=open]:text-fuul-blue',
                  )}
                >
                  {def.label}
                </span>
              )}
              {isActive && stringValue && (
                <span
                  className={cn(
                    'tw:block tw:min-w-0 tw:truncate tw:font-semibold tw:font-mono',
                    isActive ? 'tw:text-fuul-blue' : 'tw:text-foreground',
                  )}
                >
                  {stringValue}
                </span>
              )}
            </div>
          </button>
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            aria-label="Clear filter"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setLocal('');
              onChange(null);
            }}
            className="tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:inline-flex tw:items-center tw:justify-center tw:h-5 tw:w-5 tw:rounded-full tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground tw:transition-colors tw:cursor-pointer tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-fuul-blue"
          >
            <X className="tw:h-3.5 tw:w-3.5" strokeWidth={2.5} aria-hidden="true" />
          </button>
        )}
      </div>
      <PopoverContent className="tw:w-[280px] tw:p-2" align="start" sideOffset={6}>
        <Input
          autoFocus
          size="sm"
          placeholder={def.placeholder ?? 'Enter address…'}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className="tw:font-mono"
        />
      </PopoverContent>
    </Popover>
  );
};
